import { graphql } from "gatsby";
import React from "react";
import { useTranslation } from "react-i18next";
import * as R from "remeda";

import Dot from "../../assets/icons/dot.svg";

type Props = {
  result: Queries.OpeningTeaserFragment["data"];
};

const OpeningTeaser: React.FC<Props> = ({ result }) => {
  const { t } = useTranslation();
  if (!result) return null;

  const { openingDate, openingHours } = result;
  const today = new Date();

  if (openingDate) {
    const openingDateObject = new Date(openingDate);

    if (today.getTime() < openingDateObject.getTime()) {
      const text = t("location.willOpen", "Opening at {{date, MM/DD/YYYY}}", { date: openingDateObject });
      return (
        <div suppressHydrationWarning={true} className="flex text-xs m-b-xs-fluid text-secondary-red-600">
          {text}
        </div>
      );
    }
  }

  if (!openingHours || openingHours.length !== 7) return null;

  const getOpeningHours: (
    dayOfWeek: number,
  ) => null | { isOpen: Maybe<boolean>; start: Maybe<string>; end: Maybe<string>; weekday: Maybe<string> } = (
    dayOfWeek: number,
  ) => {
    if (!openingHours) return null;
    if (dayOfWeek < 0) return null;
    if (dayOfWeek > 6) return getOpeningHours(dayOfWeek - 7);

    return (
      R.pipe(
        openingHours,
        R.filter(
          (item) =>
            item?.weekday === ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dayOfWeek],
        ),
        R.first(),
      ) ?? null
    );
  };

  for (let i = 0; i < 7; i++) {
    const openingHours = getOpeningHours(today.getDay() + i);
    if (!openingHours?.isOpen || !openingHours.start || !openingHours.end) continue;

    const start = new Date(openingHours.start);
    const end = new Date(openingHours.end);

    if (i === 0) {
      if (
        today.getHours() < start.getHours() ||
        (today.getHours() === start.getHours() && today.getMinutes() < start.getMinutes())
      ) {
        const text = t("location.opensToday", "Opens today at {{date, HH:mm}} o'clock", { date: start });
        return (
          <div className="flex w-full text-xs font-normal leading-normal md:inline-grid m-b-xs-fluid text-black/70">
            <span className="flex items-center mr-2 capitalize text-secondary-red-600">
              <Dot height="10" width="9" className="self-center mr-2" />
              {t("location.closed", "Closed")}
            </span>
            <span>{text}</span>
          </div>
        );
      }

      if (
        today.getHours() < end.getHours() ||
        (today.getHours() === end.getHours() && today.getMinutes() < end.getMinutes())
      ) {
        const text = t("location.closesToday", "Open today till {{date, HH:mm}} o'clock", { date: end });
        return (
          <div className="flex items-center text-xs m-b-xs-fluid text-system-green-500" suppressHydrationWarning={true}>
            <Dot height="10" width="9" className="self-center mt-px mr-2" />
            <span>{text}</span>
          </div>
        );
      }
    }

    if (i === 1) {
      const text = t("location.opensTomorrow", "Opens tomorrow at {{date, HH:mm}} o'clock", { date: start });
      return (
        <div
          className="flex w-full text-xs font-normal leading-normal md:inline-grid m-b-xs-fluid text-black/70"
          suppressHydrationWarning={true}
        >
          <span className="flex items-center mr-2 capitalize text-secondary-red-600">
            <Dot height="10" width="9" className="self-center mr-2" />
            {t("location.closed", "Closed")}
          </span>
          <span>{text}</span>
        </div>
      );
    }

    if (i > 1) {
      const text = t("location.opensAtDay", "Opens on {{weekday, dddd}} at {{date, HH:mm}} o'clock", {
        weekday: new Date(today.getTime() + i * 1000 * 3600 * 24),
        date: start,
      });
      return (
        <div
          className="flex w-full text-xs font-normal leading-normal md:inline-grid m-b-xs-fluid text-black/70"
          suppressHydrationWarning={true}
        >
          <span className="flex items-center mr-2 capitalize text-secondary-red-600">
            <Dot height="10" width="9" className="self-center mr-2" />
            {t("location.closed", "Closed")}
          </span>
          <span>{text}</span>
        </div>
      );
    }
  }

  return null;
};

export const query = graphql`
  fragment OpeningTeaser on PrismicLocation {
    data {
      openingDate: opening_date
      openingHours: opening_hours {
        isOpen: is_open
        end
        start
        weekday
      }
    }
  }
`;

export default OpeningTeaser;
